// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { DocumentDownloadDialogComponent } from '../document-download-dialog/document-download-dialog.component';
import { SettingsService } from "src/app/modules/settings/settings.service";

/**
 * Script start
 */

@Component({
  selector: "app-property-detail-mapping-delete-dialog",
  templateUrl: "./mapping-delete-dialog.component.html",
  styleUrls: ["./mapping-delete-dialog.component.scss"]
})
export class PropertyDetailMappingDeleteDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner
  noDocs = false;
  docs: any[] = [];
  propertyId = "";
  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailMappingDeleteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private settingsService: SettingsService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    if (this.data) {
      if (typeof this.data.docs !== "undefined" && typeof this.data.docs.documents !== "undefined") {
        const obj = this.data.docs.documents;
        this.propertyId = this.data.propertyId;

        this.subscriptions.push(
          this.settingsService.getDocument().subscribe(
            (res) => {
              const translationMap: { [k: string]: { it: string; en: string } } = {};

              if (
                res.status &&
                res.data &&
                Array.isArray(res.data) &&
                res.data.length > 0 &&
                res.data[0].area &&
                Array.isArray(res.data[0].area)
              ) {
                // Ciclo le aree del template documenti per costruire la mappa key -> { it, en }
                for (const area of res.data[0].area) {
                  if (area && area.content && Array.isArray(area.content)) {
                    for (const contentItem of area.content) {
                      if (contentItem && contentItem.key && contentItem.translation) {
                        translationMap[contentItem.key] = {
                          it: contentItem.translation.it,
                          en: contentItem.translation.en,
                        };
                      }
                      // Le key dei singoli documenti (es. AC1, AC2, ...) stanno in contentItem.documents
                      if (contentItem && contentItem.documents && Array.isArray(contentItem.documents)) {
                        for (const docItem of contentItem.documents) {
                          if (docItem && docItem.key && docItem.translation) {
                            translationMap[docItem.key] = {
                              it: docItem.translation.it,
                              en: docItem.translation.en,
                            };
                          }
                        }
                      }
                    }
                  }
                }
              }

              for (const key in obj) {
                if (Array.isArray(obj[key]) && obj[key].length) {
                  obj[key].forEach((el: any) => {
                    if (el.data) {
                      this.noDocs = true;
                      const translation = translationMap[key];
                      const title = translation
                        && (this.lang === "it" ? translation.it : translation.en);
                      this.docs.push({
                        title: title,
                        key: key,
                        subDocId: el._id,
                        content: el.data.contenutoDocumento,
                        docDate: el.data.dataDocumento,
                        value: false,
                        document: el.data.document,
                      });
                    }
                  });
                }
              }
            },
            (err) => {
              console.error("Errore nel caricamento del template documenti:", err);
            }
          )
        );
      }
    }

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }


  /**
   * Handle dialog confirmation
   *
   * @since 1.0.0
   */
  onConfirmation(data?: any): void {
    const docs = this.docs.filter((el: any) => {
      return el.value;
    })
    this.dialogRef.close(docs);
  }

  /**
   * Handle dialog denial
   *
   * @since 1.0.0
   */
  onDenial(data?: any): void {
    this.dialogRef.close();
  }

    /**
   * Handle click on document form to download file
   * itemData: data value of form items
   *
   * @since 1.0.0
   */
  onToggleDownloadDocument(
    documents: any,
  ): void {
    if (!documents || !documents.length) {
      console.warn("ERROR: NO DOCS");
      return;
    }
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        propertyId: this.propertyId,
        documents,
      },
      width: "600px"
    });
  }
}
